/**
 * MiniProjectSettingsModal — DEV-901 stage 3e.
 *
 * Consolidates all Lead-editable Mini-Project settings into one modal,
 * removing the inline visibility toggles and transition buttons that
 * were cluttering the header / Progress tab (Skip's 2026-05-19 redirect).
 *
 * Chrome contract per CSS module: fixed height, sticky header + footer,
 * scrollable body. Mirrors Full Project ProjectConfigModal for cross-
 * tier consistency (Mini-Project is the training ground for Full Project).
 *
 * Save model: explicit Save / Cancel buttons in the footer. Field-level
 * edits accumulate in local state; Save sends one PUT with everything
 * changed; Cancel discards (with confirm if dirty). Lifecycle transitions
 * (Activate / Complete / Mark Expired) and Archive are POST actions on
 * their own endpoints, so they fire immediately when clicked — not on
 * Save — and trigger a parent refetch.
 *
 * Sections:
 *   1. Identity     — Title, Short Description, Description, Hero, Category
 *   2. Schedule     — Start / End dates
 *   3. Budget       — Planned amount + currency
 *   4. Status       — Lifecycle (badge + transitions), Content Status
 *   5. Location     — Location text, Lat, Lon (overrides community default)
 *   6. Visibility   — MP visibility, Progress visibility (cap-enforced)
 *   7. Community Metadata — placeholder for future schema-driven renderer
 *   8. Read-only    — Owner Community Guid, Lead, Playbook attribution
 *   9. Danger Zone  — Archive / Restore
 *
 * NOTE: Strings hardcoded pending the Mini-Project i18n sweep (DEV-1062).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import L from "leaflet";
import {
  updateMiniProject,
  transitionMiniProject,
  transferMiniProjectOwnership,
  archiveMiniProject,
  unarchiveMiniProject,
  type MiniProject,
  type UpdateMiniProjectRequest,
} from "../../api/miniProjects";
import { useApiCommunityData } from "../../api/useApiCommunityData";
import { useApiSecurityData } from "../../api/useApiSecurityData";
import { isUserSystemAdmin } from "../../security/securityAdapter";
import { useV2CommunityRoles } from "../../security/v2/useV2CommunityRoles";
import GeoLocationPicker from "../../Components/GeoLocationPicker/GeoLocationPicker";
import MiniProjectTeamTab from "../../Components/MiniProjectSettingsModal/MiniProjectTeamTab";
import MiniProjectMetadataTab, {
  type MiniProjectMetadataTabHandle,
} from "../../Components/MiniProjectSettingsModal/MiniProjectMetadataTab";
import { computeMetadataNeedsValuesCount } from "../../Data/CommunityMetadata/metadataNeedsValues";
import {
  persistMiniProjectTeamToApi,
  discardLocalEdits as discardTeamLocalEdits,
} from "../../Data/MiniProjectConfig/miniProjectTeam";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { MINI_LIFECYCLE_TO_PROJECT_STATUS } from "../../Data/MiniProjectConfig/miniProjectLifecycle";
import styles from "./MiniProjectSettingsModal.module.css";

type TabKey = "general" | "visibility" | "team" | "metadata" | "danger";

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

/** Mirror of ProjectConfigModal.isOrgEntity. Geographies and uncategorized
 *  rows are excluded; organization roots (entity_type='organization' from
 *  pre-taxonomy migrations) and the new org_*-prefixed types are accepted.
 *  See POLICY_PROJECT_OWNERSHIP_TRANSFER.md §1. */
const isOrgEntity = (e: any): boolean => {
  const t = String(e?.entity_type ?? "").toLowerCase();
  if (!t) return false;
  if (t.startsWith("geo_")) return false;
  return t.startsWith("org_") || t === "org" || t === "organization";
};

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const CONTENT_STATUS_OPTIONS = [
  "Drafting",
  "Under Review",
  "Peer Reviewed",
  "Published",
  "Archived",
] as const;

/** User-facing project status — mirrors Full Project projectStatusOptions
 *  for cross-tier vocabulary consistency. Migration 168. */
const PROJECT_STATUS_OPTIONS = [
  "Proposed",
  "Planned",
  "In Progress",
  "Abandoned",
  "Completed",
] as const;

/** Minimal currency picker list. Full ISO 4217 set is hundreds of codes;
 *  these cover the common cases. Lead can type any 3-letter code via
 *  the "Other" option which falls back to a text input. */
const COMMON_CURRENCIES = [
  "USD", "EUR", "GBP", "CAD", "AUD", "JPY", "CHF", "CNY",
  "INR", "MXN", "BRL", "ZAR", "SEK", "NOK", "DKK",
];

const CATEGORY_OPTIONS = [
  "Education",
  "Environment",
  "Health",
  "Community",
  "Food Security",
  "Water",
  "Energy",
  "Equality",
  "Peace",
  "Infrastructure",
];

type Visibility = "team_only" | "community" | "public";
const VISIBILITY_OPTIONS: { value: Visibility; label: string }[] = [
  { value: "team_only", label: "Team Only" },
  { value: "community", label: "Community" },
  { value: "public", label: "Public" },
];
const VIS_ORDINAL: Record<Visibility, number> = {
  team_only: 0,
  community: 1,
  public: 2,
};

// DEV-1271: consolidated into the shared lifecycle map.
const LIFECYCLE_LABEL = MINI_LIFECYCLE_TO_PROJECT_STATUS;

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

interface Props {
  isOpen: boolean;
  onClose: () => void;
  miniProject: MiniProject;
  isLead: boolean;
  /** Logged-in user's GUID — used to compute which communities the
   *  Lead is allowed to transfer ownership to. */
  currentUserGuid: string;
  /** Called after any persisted change so the parent can refetch. */
  onSaved: () => void;
  /** U1b (DEV-1265): when provided, the "Mark Completed" transition routes
   *  to the page-level completion dialog instead of completing inline. */
  onRequestComplete?: () => void;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function MiniProjectSettingsModal({
  isOpen,
  onClose,
  miniProject,
  isLead,
  currentUserGuid,
  onSaved,
  onRequestComplete,
}: Props) {
  // ── Editable field state — initialized from miniProject ──
  // Description and Hero are no longer settable here per Skip's
  // 2026-05-19 redirect: there is only ONE description (the field
  // labeled "Description" below, stored as shortDescription), and
  // hero photo editing belongs in the page header.
  const [title, setTitle] = useState(miniProject.title);
  const [shortDesc, setShortDesc] = useState(miniProject.shortDescription || "");
  const [ownerCommunityGuid, setOwnerCommunityGuid] = useState(miniProject.ownerCommunityGuid);
  const [categoryKey, setCategoryKey] = useState(miniProject.categoryKey || "");
  const [startDate, setStartDate] = useState(miniProject.startDate || "");
  const [endDate, setEndDate] = useState(miniProject.endDate || "");
  const [plannedBudgetAmount, setPlannedBudgetAmount] = useState(
    miniProject.plannedBudgetAmount != null ? String(miniProject.plannedBudgetAmount) : "",
  );
  const [plannedBudgetCurrency, setPlannedBudgetCurrency] = useState(
    miniProject.plannedBudgetCurrency || "USD",
  );
  const [contentStatus, setContentStatus] = useState(miniProject.contentStatus || "");
  const [projectStatus, setProjectStatus] = useState(miniProject.projectStatus || "");
  // Task #218. Selected metadata schema. Empty string = none selected.
  // Persisted to mini_projects.metadata_schema_guid via the Update endpoint;
  // a Guid.Empty sentinel on the wire clears the field server-side, undefined
  // leaves it unchanged.
  const [metadataSchemaGuid, setMetadataSchemaGuid] = useState(
    miniProject.metadataSchemaGuid || "",
  );
  const [locationGuid, setLocationGuid] = useState(miniProject.locationGeographyGuid || "");
  const [locationLat, setLocationLat] = useState(
    miniProject.locationLat != null ? String(miniProject.locationLat) : "",
  );
  const [locationLon, setLocationLon] = useState(
    miniProject.locationLon != null ? String(miniProject.locationLon) : "",
  );
  const [visibility, setVisibility] = useState<Visibility>(miniProject.visibility);
  const [progressVisibility, setProgressVisibility] = useState<Visibility>(
    miniProject.progressVisibility,
  );

  const [saving, setSaving] = useState(false);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Task #219. Imperative handle to the Metadata tab so the modal's Save
  // can call persist() once. Dirty-check on Cancel reads handle.isDirty.
  const metadataTabRef = useRef<MiniProjectMetadataTabHandle>(null);

  // Tab routing. Default to General on open; reset on each open via the
  // re-seed effect below. POLICY_TEAM_ROSTERS.md §5.3.
  const { t: tProjectConfig } = useAppTranslation("projectConfig");
  const [activeTab, setActiveTab] = useState<TabKey>("general");

  // Team tab dirty signal. Bumped by the Team tab via onDirty; cleared
  // on successful save or cancel. Folded into isDirty so the Save button
  // becomes enabled when only team edits are pending.
  const [teamDirty, setTeamDirty] = useState(false);
  const handleTeamDirty = useCallback(() => setTeamDirty(true), []);

  // Metadata tab dirty signal (DEV-1282 fix). The tab's isDirty lives behind
  // a ref, which doesn't re-render the parent — so a metadata-only change
  // left Save disabled. The tab now reports its dirty state via onDirty;
  // folded into isDirty below.
  const [metadataDirty, setMetadataDirty] = useState(false);

  // ── Map pin (mirrors Full Project ProjectConfigModal Basics tab) ──
  // Click the map to place / drag the marker to adjust. Lat/Lon text
  // fields below stay editable for direct numeric entry. The marker
  // and the text fields stay in sync via the marker-sync useEffect.
  const miniMapContainerRef = useRef<HTMLDivElement | null>(null);
  const miniMapRef = useRef<L.Map | null>(null);
  const miniMarkerRef = useRef<L.Marker | null>(null);

  /** Parse the two text-input lat/lon values into a usable pair or null. */
  const parseLatLon = (latRaw: string, lonRaw: string): { lat: number; lon: number } | null => {
    const lat = Number(String(latRaw ?? "").trim());
    const lon = Number(String(lonRaw ?? "").trim());
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
    if (lat < -90 || lat > 90) return null;
    if (lon < -180 || lon > 180) return null;
    if (lat === 0 && lon === 0) return null;  // treat 0,0 as "unset"
    return { lat, lon };
  };

  /** Write map-derived lat/lon back into the text inputs (state). */
  const setLatLonFromMap = (lat: number, lon: number) => {
    // Leaflet can return longitudes outside ±180° when the map wraps.
    // Normalize to the standard [-180, 180] range.
    const normLon = ((((lon + 180) % 360) + 360) % 360) - 180;
    const fmt = (v: number) => (Math.round(v * 1_000_000) / 1_000_000).toString();
    setLocationLat(fmt(lat));
    setLocationLon(fmt(normLon));
  };

  /** Small unobtrusive marker — same visual as Full Project. */
  const makeMiniMapIcon = () => L.divIcon({
    className: "",
    html: '<div style="width:16px;height:16px;border-radius:50%;background:#38bdf8;border:2px solid rgba(255,255,255,0.9);box-shadow:0 6px 14px rgba(0,0,0,0.35);"></div>',
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });

  // Community data — entities + relationships drive the Owner picker and
  // the Primary Location picker. Matches Full Project ProjectConfigModal's
  // data flow for cross-tier consistency.
  // Task #218. `schemas` powers the Metadata Schema picker on the General
  // tab. Mirrors the FP path in ProjectConfigModal which reads
  // apiCommunityData.schemas directly — server-side filtering already
  // limits what the caller can see, so no client-side org-root walking
  // is needed.
  const { communityEntities, communityRelationships, schemas } = useApiCommunityData();
  const securityData = useApiSecurityData();

  // Task #218. Sorted schema list for the Metadata Schema picker. Mirrors
  // the FP schemaOptions memo in ProjectConfigModal.tsx — server-side
  // filtering already limits the array to schemas the caller can see, so
  // no client-side org-root walking is needed.
  const schemaOptions = useMemo(() => {
    const base = Array.isArray(schemas) ? schemas.slice() : [];
    return base.sort((a: any, b: any) =>
      String(a?.schema_name ?? "").localeCompare(String(b?.schema_name ?? "")),
    );
  }, [schemas]);

  // Pull additional fields off useApiCommunityData for the tab-label
  // "needs values" badge. Cheap to keep alongside `schemas` — already
  // hydrated for the modal anyway.
  const { schemaFields, hierarchies, metadataValues } = useApiCommunityData();

  // Task #220. Badge count = hierarchy-participating fields with no value
  // for this MP on the SELECTED schema (the one in local state, not the
  // server-side value). Recomputes when the user changes the schema picker
  // so the badge reflects the new schema's required fields immediately.
  const metadataNeedsValuesCount = useMemo(
    () =>
      computeMetadataNeedsValuesCount(
        miniProject.miniProjectGuid,
        "mini_project",
        metadataSchemaGuid,
        schemaFields,
        hierarchies as any,
        metadataValues,
      ),
    [
      miniProject.miniProjectGuid,
      metadataSchemaGuid,
      schemaFields,
      hierarchies,
      metadataValues,
    ],
  );

  // 2FA enrollment status — required by useV2CommunityRoles to honor the
  // requires_2fa gate on capability bundles. Mirrors CommunitiesPage.tsx:208-214.
  const is2FAEnabled = useMemo(() => {
    if (!currentUserGuid) return false;
    const me = currentUserGuid.toUpperCase();
    const user = (securityData.securityUsers || []).find(
      (u: any) => String(u.user_guid).toUpperCase() === me,
    );
    return !!user?.is_two_factor_enabled;
  }, [securityData.securityUsers, currentUserGuid]);

  // Resolve the Lead's user_guid → display name for the read-only field
  // in the Project Info section. The wire field MiniProject.ownerUserGuid
  // still carries the Lead's user_guid post-migration 172. Falls back to
  // a GUID prefix when securityUsers hasn't loaded or the user record is
  // unavailable (deactivated account with a still-active team row).
  // DEV-932: securityUsers is async; the dep list includes it so this
  // memo re-runs when the snapshot arrives.
  const leadDisplayName = useMemo(() => {
    const leadGuid = (miniProject.ownerUserGuid || "").toUpperCase();
    if (!leadGuid) return "";
    const u = (securityData.securityUsers || []).find(
      (su: any) => String(su.user_guid).toUpperCase() === leadGuid,
    ) as any;
    if (!u) return `${leadGuid.slice(0, 8)}…`;
    return u.display_name || u.name || u.screen_name || u.email || `${leadGuid.slice(0, 8)}…`;
  }, [securityData.securityUsers, miniProject.ownerUserGuid]);

  // V2 evaluator — returns a Map of community GUID → capability Set,
  // with parent-chain inheritance already applied by the evaluator.
  // See POLICY_PROJECT_OWNERSHIP_TRANSFER.md §3.
  const { capsByCommunity: v2Caps } = useV2CommunityRoles({
    userGuid: currentUserGuid || "",
    is2FAEnabled,
    // Cast: the hook's structural type ({ entity_guid: string } / { child_entity_guid, parent_entity_guid })
    // matches the seed-shape rows the API hook returns, but TS sees the hook
    // return as Record<string, any>[]. Same runtime shape as CommunitiesPage uses.
    entities: (Array.isArray(communityEntities) ? communityEntities : []) as any,
    relationships: (Array.isArray(communityRelationships) ? communityRelationships : []) as any,
  });

  // Eligible owner communities — per POLICY_PROJECT_OWNERSHIP_TRANSFER.md §1:
  //   "Any organization community (entity_type='org_*' or 'organization')
  //    on which the initiator holds 'community:admin' or 'community:create_projects'
  //    either directly or by inheritance through the parent chain."
  //
  // Authority sources, in priority order:
  //   (a) System-admin bypass — sysadmins see every active org.
  //   (b) V2 grants via useV2CommunityRoles — the evaluator already walks
  //       the parent chain, so direct + inherited grants are unified.
  //   (c) V1 community_role_assignments — legacy fallback during the
  //       DEV-876 transition window. Direct role + ancestor walk.
  //   (d) The current owner is always included so the dropdown can render
  //       even if the user has somehow lost authority since project creation.
  //
  // Phase 1 of POLICY implementation. Phase 2 (auto-commit for admins) and
  // Phase 3 (approval workflow for content_creators) come later. This filter
  // only controls dropdown contents — approval gating happens at transfer time.
  const eligibleOwnerCommunities = useMemo(() => {
    // ---- 1. Build candidate set: every active org community ----
    const allOrgs: { entity_guid: string; name: string }[] = [];
    const entities = Array.isArray(communityEntities) ? communityEntities : [];
    for (const e of entities) {
      if (!isOrgEntity(e)) continue;
      if ((e as any)?.is_active === false) continue;
      const g = String((e as any)?.entity_guid || "").toUpperCase();
      const name = String((e as any)?.name ?? "").trim();
      if (!g || !name) continue;
      allOrgs.push({ entity_guid: g, name });
    }
    allOrgs.sort((a, b) => a.name.localeCompare(b.name));

    // ---- 2. System-admin fast-path ----
    if (currentUserGuid && isUserSystemAdmin(currentUserGuid as any)) {
      return allOrgs;
    }

    const okGuids = new Set<string>();

    // ---- 3. V2 grants (parent cascade already applied by evaluator) ----
    // Per POLICY §1, the qualifying caps are 'community:admin' (owner/admin)
    // and 'community:create_projects' (content_creator equivalent).
    for (const [guid, caps] of v2Caps) {
      if (caps.has("community:admin") || caps.has("community:create_projects")) {
        okGuids.add(guid);
      }
    }

    // ---- 4. V1 legacy with ancestor walk (transition fallback) ----
    // Drop after DEV-876 retires the legacy admin UI. Without this branch,
    // any user provisioned only via legacy admin tools would be invisible.
    const me = (currentUserGuid || "").toUpperCase();
    const allowedV1 = new Set(["content_creator", "admin", "owner"]);
    const directV1 = new Set<string>();
    const v1Roles = Array.isArray(securityData.communityRoles) ? securityData.communityRoles : [];
    for (const r of v1Roles) {
      if (String((r as any).user_guid || "").toUpperCase() !== me) continue;
      if (!allowedV1.has(String((r as any).role || "").toLowerCase())) continue;
      directV1.add(String((r as any).community_entity_guid || "").toUpperCase());
    }

    // Cycle-safe upward walk: for each org, walk parents until we find a
    // direct V1 row or exhaust the chain.
    const parentsByChild = new Map<string, string[]>();
    const rels = Array.isArray(communityRelationships) ? communityRelationships : [];
    for (const rel of rels) {
      const child = String((rel as any)?.child_entity_guid || "").toUpperCase();
      const parent = String((rel as any)?.parent_entity_guid || "").toUpperCase();
      if (!child || !parent) continue;
      const list = parentsByChild.get(child);
      if (list) list.push(parent);
      else parentsByChild.set(child, [parent]);
    }
    const hasV1Authority = (rootGuid: string): boolean => {
      const seen = new Set<string>();
      const stack: string[] = [rootGuid];
      while (stack.length > 0) {
        const cur = stack.pop()!;
        if (seen.has(cur)) continue;
        seen.add(cur);
        if (directV1.has(cur)) return true;
        const parents = parentsByChild.get(cur);
        if (parents) for (const p of parents) stack.push(p);
      }
      return false;
    };
    for (const org of allOrgs) {
      if (okGuids.has(org.entity_guid)) continue;
      if (hasV1Authority(org.entity_guid)) okGuids.add(org.entity_guid);
    }

    // ---- 5. Always include the current owner ----
    okGuids.add((miniProject.ownerCommunityGuid || "").toUpperCase());

    return allOrgs.filter((o) => okGuids.has(o.entity_guid));
  }, [
    communityEntities,
    communityRelationships,
    securityData.communityRoles,
    v2Caps,
    currentUserGuid,
    miniProject.ownerCommunityGuid,
  ]);

  // ── Initialize the Leaflet map when the modal opens ──
  // Container exists only while isOpen=true (it's inside the modal JSX
  // tree below). We init once per open cycle and tear down on close.
  // Centered on continental US (39, -98) at zoom 4 when no lat/lon is
  // set; centered on the existing pin at zoom 11 otherwise.
  useEffect(() => {
    if (!isOpen) return;
    if (!miniMapContainerRef.current) return;
    if (miniMapRef.current) {
      // Already initialized — just nudge Leaflet to recompute size in case
      // the modal layout shifted (e.g. tab-like flex resize).
      setTimeout(() => miniMapRef.current?.invalidateSize(), 0);
      return;
    }

    const existing = parseLatLon(locationLat, locationLon);
    const defaultCenter: [number, number] = existing
      ? [existing.lat, existing.lon]
      : [39.0, -98.0];
    const defaultZoom = existing ? 11 : 4;

    const map = L.map(miniMapContainerRef.current, {
      zoomControl: true,
      attributionControl: false,
    }).setView(defaultCenter, defaultZoom);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
    }).addTo(map);

    const marker = L.marker(defaultCenter, {
      draggable: true,
      icon: makeMiniMapIcon(),
    }).addTo(map);

    marker.on("dragend", () => {
      const ll = marker.getLatLng();
      setLatLonFromMap(ll.lat, ll.lng);
    });

    map.on("click", (e: L.LeafletMouseEvent) => {
      const ll = e?.latlng;
      if (!ll) return;
      marker.setLatLng(ll);
      setLatLonFromMap(ll.lat, ll.lng);
    });

    miniMapRef.current = map;
    miniMarkerRef.current = marker;

    setTimeout(() => map.invalidateSize(), 0);
    // Intentional partial deps — we only init on open, not on every
    // lat/lon edit. The marker-sync effect below handles updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Keep the marker in sync with text-input lat/lon edits.
  useEffect(() => {
    if (!isOpen || !miniMapRef.current || !miniMarkerRef.current) return;
    const parsed = parseLatLon(locationLat, locationLon);
    if (!parsed) return;
    const ll = L.latLng(parsed.lat, parsed.lon);
    miniMarkerRef.current.setLatLng(ll);
    miniMapRef.current.panTo(ll, { animate: false });
  }, [locationLat, locationLon, isOpen]);

  // Cleanup map on modal close (Leaflet binds to the DOM container; if
  // the modal unmounts/remounts we need to recreate the map next open).
  useEffect(() => {
    if (isOpen) return;
    if (miniMapRef.current) {
      try { miniMapRef.current.remove(); } catch { /* ignore */ }
      miniMapRef.current = null;
      miniMarkerRef.current = null;
    }
  }, [isOpen]);

  // ── Re-seed local state when the modal opens with a fresh miniProject ──
  const prevOpenRef = useRef(isOpen);
  useEffect(() => {
    if (isOpen && !prevOpenRef.current) {
      setTitle(miniProject.title);
      setShortDesc(miniProject.shortDescription || "");
      // Normalize to uppercase so the dropdown's option values
       // (also normalized) match — avoids the silent option[0] fallback.
      setOwnerCommunityGuid((miniProject.ownerCommunityGuid || "").toUpperCase());
      setCategoryKey(miniProject.categoryKey || "");
      setStartDate(miniProject.startDate || "");
      setEndDate(miniProject.endDate || "");
      setPlannedBudgetAmount(
        miniProject.plannedBudgetAmount != null ? String(miniProject.plannedBudgetAmount) : "",
      );
      setPlannedBudgetCurrency(miniProject.plannedBudgetCurrency || "USD");
      setContentStatus(miniProject.contentStatus || "");
      setProjectStatus(miniProject.projectStatus || "");
      // DEV-1210 — Normalize to uppercase to match schemaOptions[i].schema_guid
      // (also uppercased via normalizeGuid in communityMetadata.ts). Without
      // this, the saved GUID (lowercase from PostgreSQL) never matched any
      // <option> value, and the <select> silently fell back to "(none)"
      // even though state still held the correct GUID — same pattern as
      // ownerCommunityGuid two lines above.
      setMetadataSchemaGuid((miniProject.metadataSchemaGuid || "").toUpperCase());
      setLocationGuid(miniProject.locationGeographyGuid || "");
      setLocationLat(miniProject.locationLat != null ? String(miniProject.locationLat) : "");
      setLocationLon(miniProject.locationLon != null ? String(miniProject.locationLon) : "");
      setVisibility(miniProject.visibility);
      setProgressVisibility(miniProject.progressVisibility);
      setError(null);
      setActiveTab("general");
      setTeamDirty(false);
    }
    prevOpenRef.current = isOpen;
  }, [isOpen, miniProject]);

  // ── Dirty detection for Cancel confirmation ──
  const isDirty = useMemo(() => {
    return (
      title !== miniProject.title ||
      shortDesc !== (miniProject.shortDescription || "") ||
      (ownerCommunityGuid || "").toUpperCase() !==
        (miniProject.ownerCommunityGuid || "").toUpperCase() ||
      categoryKey !== (miniProject.categoryKey || "") ||
      startDate !== (miniProject.startDate || "") ||
      endDate !== (miniProject.endDate || "") ||
      plannedBudgetAmount !==
        (miniProject.plannedBudgetAmount != null ? String(miniProject.plannedBudgetAmount) : "") ||
      plannedBudgetCurrency !== (miniProject.plannedBudgetCurrency || "USD") ||
      contentStatus !== (miniProject.contentStatus || "") ||
      projectStatus !== (miniProject.projectStatus || "") ||
      // DEV-1210 — Both sides uppercased to mirror the ownerCommunityGuid
      // pattern above. metadataSchemaGuid state is normalized at seed time
      // (line 552) so the API row's lowercase value would otherwise compare
      // !== to the uppercase state and trigger a false "dirty" reading the
      // moment the modal opens.
      (metadataSchemaGuid || "").toUpperCase() !==
        (miniProject.metadataSchemaGuid || "").toUpperCase() ||
      locationGuid !== (miniProject.locationGeographyGuid || "") ||
      locationLat !== (miniProject.locationLat != null ? String(miniProject.locationLat) : "") ||
      locationLon !== (miniProject.locationLon != null ? String(miniProject.locationLon) : "") ||
      visibility !== miniProject.visibility ||
      progressVisibility !== miniProject.progressVisibility ||
      teamDirty ||
      metadataDirty
    );
  }, [
    title, shortDesc, ownerCommunityGuid, categoryKey, startDate, endDate,
    plannedBudgetAmount, plannedBudgetCurrency, contentStatus, projectStatus,
    metadataSchemaGuid,
    locationGuid, locationLat, locationLon, visibility, progressVisibility,
    teamDirty, metadataDirty,
    miniProject,
  ]);

  // ── Visibility cap UX — disable Progress options > MP visibility ──
  const mpOrdinal = VIS_ORDINAL[visibility];

  /* ── Save ──
   *
   * POLICY_PROJECT_OWNERSHIP_TRANSFER.md Phase 2. Owner-community change
   * is no longer carried in the PUT body — the backend rejects it there.
   * Path: detect a change client-side and call /transfer-ownership FIRST.
   * If transfer succeeds (or there was no change), proceed with the PUT
   * for the rest of the fields. If transfer fails with approval_required,
   * surface a clear message and DON'T proceed with the PUT — the user's
   * intent was a transfer that the system isn't ready to honor yet
   * (Phase 3 will land that workflow).
   */
  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      // Step 1: ownership transfer (if any).
      const ownerChanged =
        (ownerCommunityGuid || "").toUpperCase() !==
        (miniProject.ownerCommunityGuid || "").toUpperCase();
      if (ownerChanged) {
        try {
          await transferMiniProjectOwnership(
            miniProject.miniProjectGuid,
            ownerCommunityGuid,
          );
        } catch (e: any) {
          const body = e?.body;
          if (body?.error === "approval_required") {
            setError(
              body.message ??
                "Transfers to this community require approval from a community owner or admin.",
            );
          } else if (body?.error === "forbidden") {
            setError(
              body.message ??
                "You do not have permission to transfer this project to that community.",
            );
          } else {
            setError(e instanceof Error ? e.message : String(e));
          }
          return; // do NOT proceed with the PUT — user's intent was the transfer
        }
      }

      // Step 2: PUT the remaining fields. ownerCommunityGuid intentionally
      // omitted — the dedicated /transfer-ownership endpoint owns that path.
      const body: UpdateMiniProjectRequest = {
        title,
        shortDescription: shortDesc,
        categoryKey,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        contentStatus,  // empty string clears (backend handles)
        projectStatus,  // empty string clears (backend handles)
        // Task #218. Send Guid.Empty to clear (the backend's PATCH semantic for
        // metadataSchemaGuid), the selected GUID to set, undefined to skip.
        metadataSchemaGuid: metadataSchemaGuid
          ? metadataSchemaGuid
          : "00000000-0000-0000-0000-000000000000",
        plannedBudgetAmount: plannedBudgetAmount.trim()
          ? Number(plannedBudgetAmount)
          : undefined,
        plannedBudgetCurrency: plannedBudgetAmount.trim()
          ? plannedBudgetCurrency
          : "",  // clear currency when amount cleared
        locationGeographyGuid: locationGuid || undefined,
        locationLat: locationLat.trim() ? Number(locationLat) : undefined,
        locationLon: locationLon.trim() ? Number(locationLon) : undefined,
        visibility,
        progressVisibility,
      };
      await updateMiniProject(miniProject.miniProjectGuid, body);

      // Step 2b (Task #219): persist Metadata tab edits. The metadata
      // schema_guid was just committed in the PUT above, so the server
      // accepts the value writes against the now-current schema. We
      // intentionally do NOT swallow errors here — if validation rejects
      // (e.g., a value_guid doesn't resolve to a community entity of the
      // expected type), the outer try/catch in this handler picks it up,
      // sets the visible error message, and skips onSaved()+onClose() so
      // the user can read what went wrong instead of seeing the modal
      // close on a silent partial-success. A prior pass wrapped this in
      // a local try/catch that left onClose to run regardless — that
      // hid the real failure and made the bug look like "values just
      // don't persist."
      if (metadataTabRef.current?.isDirty) {
        await metadataTabRef.current.persist();
      }

      // Step 3: persist team edits (if any) via the bulk endpoint.
      // The team in-memory store carries the intended state; this single
      // call diffs server-side and applies adds/updates/removes/lead-transfer
      // atomically. Failures here are surfaced like other save errors and
      // DO NOT close the modal — the admin can fix and retry.
      if (teamDirty) {
        await persistMiniProjectTeamToApi(miniProject.miniProjectGuid);
        setTeamDirty(false);
      }

      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [
    title, shortDesc, ownerCommunityGuid, categoryKey, startDate, endDate,
    contentStatus, projectStatus, plannedBudgetAmount, plannedBudgetCurrency,
    metadataSchemaGuid,
    locationGuid, locationLat, locationLon, visibility, progressVisibility,
    teamDirty,
    miniProject.miniProjectGuid, miniProject.ownerCommunityGuid, onSaved, onClose,
  ]);

  /* ── Cancel ── */
  const handleCancel = useCallback(() => {
    if (isDirty) {
      if (!window.confirm("Discard unsaved changes?")) return;
    }
    // Reset the in-memory team store so any local-only adds vanish and
    // the next modal open re-hydrates from the server. Without this,
    // canceled edits would persist in memory and re-appear on the next
    // open of the Team tab.
    if (teamDirty) {
      discardTeamLocalEdits(miniProject.miniProjectGuid);
      setTeamDirty(false);
    }
    onClose();
  }, [isDirty, teamDirty, miniProject.miniProjectGuid, onClose]);

  /* ── Lifecycle transitions ── */
  // DEV-1271: completion is confirmed (stopgap until U1b's completion
  // dialog) and now reversible (Reopen / un-start). Reverse moves are
  // low-stakes — nothing is lost, playbook & retrospective persist — so
  // they get a lighter confirm.
  const doTransition = useCallback(
    async (newStatus: "active" | "complete" | "expired" | "draft") => {
      if (
        newStatus === "complete" &&
        !window.confirm(
          "Mark this Mini-Project complete?\n\nKPI indicators turn blue, the completion date is recorded, and you'll be prompted to write a retrospective and (optionally) create a playbook. You can Reopen it later if you need to.",
        )
      ) {
        return;
      }
      if (
        newStatus === "draft" &&
        !window.confirm(
          "Move this Mini-Project back to planning?\n\nIt returns to Draft and the activation date is cleared. Your tasks, goals, and team are kept.",
        )
      ) {
        return;
      }
      setBusyAction(`transition:${newStatus}`);
      setError(null);
      try {
        await transitionMiniProject(miniProject.miniProjectGuid, { newStatus });
        onSaved();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setBusyAction(null);
      }
    },
    [miniProject.miniProjectGuid, onSaved],
  );

  /* ── Archive / Restore ── */
  const isArchived = miniProject.archivedAt != null;
  const doArchive = useCallback(async () => {
    if (
      !window.confirm(
        "Archive this Mini-Project?\n\nIt will be hidden from default lists. The project's status (" +
          miniProject.status +
          ") stays the same. You can restore it any time.",
      )
    )
      return;
    setBusyAction("archive");
    setError(null);
    try {
      await archiveMiniProject(miniProject.miniProjectGuid);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyAction(null);
    }
  }, [miniProject.miniProjectGuid, miniProject.status, onSaved]);

  const doUnarchive = useCallback(async () => {
    setBusyAction("unarchive");
    setError(null);
    try {
      await unarchiveMiniProject(miniProject.miniProjectGuid);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyAction(null);
    }
  }, [miniProject.miniProjectGuid, onSaved]);

  /* ── Available lifecycle transitions for the current status ──
     DEV-1271: completion/expiry are no longer terminal — Reopen returns
     them to active; active can step back to Draft (un-start). */
  const transitionButtons: {
    label: string;
    target: "active" | "complete" | "expired" | "draft";
    variant?: "reverse";
  }[] = [];
  switch (miniProject.status) {
    case "draft":
      transitionButtons.push({ label: "Activate", target: "active" });
      break;
    case "active":
      transitionButtons.push({ label: "Mark Completed", target: "complete" });
      transitionButtons.push({ label: "Mark Expired", target: "expired" });
      transitionButtons.push({ label: "Back to planning", target: "draft", variant: "reverse" });
      break;
    case "complete":
      transitionButtons.push({ label: "Reopen project", target: "active", variant: "reverse" });
      break;
    case "expired":
      transitionButtons.push({ label: "Reopen project", target: "active", variant: "reverse" });
      break;
  }

  if (!isOpen) return null;
  if (!isLead) return null;  // Modal is Lead-only; trigger should already gate

  /* ────────────────────────────────────────────────────────── */

  const modal = (
    <div
      className={styles.backdrop}
      onClick={(e) => {
        if (e.target === e.currentTarget) handleCancel();
      }}
    >
      <div className={styles.modal} role="dialog" aria-modal="true" aria-label="Mini-Project Settings">
        {/* ── Header ── */}
        <header className={styles.header}>
          <h2 className={styles.title}>Mini-Project Settings</h2>
          <button type="button" className={styles.close} onClick={handleCancel} aria-label="Close">
            ×
          </button>
        </header>

        {/* ── Tabs row ──
            POLICY_TEAM_ROSTERS.md §5.3 — Settings editing happens in
            tabs; detail pages stay read-only. Pattern mirrors
            CommunitySettingsModal for cross-tier consistency. */}
        <div className={styles.tabs}>
          <button
            type="button"
            className={activeTab === "general" ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab("general")}
          >
            {tProjectConfig("miniProjectSettings.tabs.general")}
          </button>
          <button
            type="button"
            className={activeTab === "visibility" ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab("visibility")}
          >
            {tProjectConfig("miniProjectSettings.tabs.visibility")}
          </button>
          <button
            type="button"
            className={activeTab === "team" ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab("team")}
          >
            {tProjectConfig("miniProjectSettings.tabs.team")}
          </button>
          <button
            type="button"
            className={activeTab === "metadata" ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab("metadata")}
          >
            {tProjectConfig("miniProjectSettings.tabs.metadata")}
            {/* Task #220. "Needs values" badge — count of hierarchy-
                participating fields with no value on the current schema. */}
            {metadataNeedsValuesCount > 0 ? (
              <span
                aria-label={`${metadataNeedsValuesCount} fields need values`}
                title="Some hierarchy fields have no value yet — set them so this project groups correctly in portfolios."
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minInlineSize: 18,
                  blockSize: 18,
                  marginInlineStart: 6,
                  padding: "0 5px",
                  borderRadius: 9,
                  background: "var(--color-warning)",
                  color: "var(--bg-surface)",
                  fontSize: 11,
                  fontWeight: 600,
                  lineHeight: 1,
                }}
              >
                {metadataNeedsValuesCount}
              </span>
            ) : null}
          </button>
          <button
            type="button"
            className={activeTab === "danger" ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab("danger")}
          >
            {tProjectConfig("miniProjectSettings.tabs.danger")}
          </button>
        </div>

        {/* ── Body (scrollable) ── */}
        <div className={styles.body}>
          {activeTab === "general" && (
          <>
          {/* ── Owner + Primary Location (top, editable) ───────────
              Mirrors Full Project Basics ordering. Owner picker shows
              communities the current user has content_creator / admin /
              owner role on (sysadmin sees all). Primary Location uses
              the shared GeoLocationPicker for cross-tier consistency.

              GUID values normalized to uppercase on both the select and
              its options so the browser's value-matching never silently
              fails on case mismatch between API responses (commonly
              lowercase) and dropdown option values. Mismatched cases
              would cause the select to fall back to option[0] and the
              user would see "owner reverted to <first alphabetical>"
              on modal reopen. */}
          <section className={styles.section}>
            <div className={styles.fieldRow}>
              <label className={styles.label}>Owner</label>
              <select
                className={styles.select}
                value={(ownerCommunityGuid || "").toUpperCase()}
                onChange={(e) => setOwnerCommunityGuid(e.target.value)}
              >
                {eligibleOwnerCommunities.length === 0 ? (
                  <option value={(miniProject.ownerCommunityGuid || "").toUpperCase()}>
                    (loading communities…)
                  </option>
                ) : (
                  eligibleOwnerCommunities.map((c: any) => {
                    const v = String(c.entity_guid || "").toUpperCase();
                    return (
                      <option key={v} value={v}>
                        {c.name}
                      </option>
                    );
                  })
                )}
              </select>
              <div className={styles.helpText}>
                Only communities where you have Content Creator, Admin, or Owner
                privileges are listed. Changing the owner has metadata-schema and
                ACL implications — the server re-validates on save.
              </div>
            </div>

            <div className={styles.fieldRow}>
              <label className={styles.label}>Primary Location</label>
              <GeoLocationPicker
                value={locationGuid}
                onChange={setLocationGuid}
                entities={Array.isArray(communityEntities) ? communityEntities : []}
                relationships={Array.isArray(communityRelationships) ? communityRelationships : []}
                placeholder="Type a city, state, or country…"
              />
              <div className={styles.helpText}>
                When set, Geography grouping is derived from this location.
                Leave blank to inherit from the owning community.
              </div>
            </div>
          </section>

          {/* ── 1. Identity ── */}
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Identity</h3>

            <div className={styles.fieldRow}>
              <label className={styles.label}>Title</label>
              <input
                className={styles.input}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Project title"
              />
            </div>

            <div className={styles.fieldRow}>
              <label className={styles.label}>Description</label>
              <textarea
                className={`${styles.textarea} ${styles.shortDescTextarea}`}
                value={shortDesc}
                onChange={(e) => setShortDesc(e.target.value)}
                placeholder="1-3 sentences. Shown on project cards and at the top of the About tab."
              />
              <div className={styles.helpText}>
                This is the same description that appears on the About tab and on
                project cards. Hero photo is edited from the page header — not here.
              </div>
            </div>

            <div className={styles.fieldRow}>
              <label className={styles.label}>Category</label>
              <select
                className={styles.select}
                value={categoryKey}
                onChange={(e) => setCategoryKey(e.target.value)}
              >
                <option value="">(none)</option>
                {CATEGORY_OPTIONS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
          </section>

          {/* ── 2. Schedule ── */}
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Schedule</h3>
            <div className={styles.grid2}>
              <div className={styles.fieldRow}>
                <label className={styles.label}>Start Date</label>
                <input
                  className={styles.input}
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className={styles.fieldRow}>
                <label className={styles.label}>End Date</label>
                <input
                  className={styles.input}
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>
          </section>

          {/* ── 3. Budget ── */}
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Budget</h3>
            <div className={styles.budgetGrid}>
              <div className={styles.fieldRow}>
                <label className={styles.label}>Planned Amount</label>
                <input
                  className={styles.input}
                  type="number"
                  step="0.01"
                  min="0"
                  value={plannedBudgetAmount}
                  onChange={(e) => setPlannedBudgetAmount(e.target.value)}
                  placeholder="0.00"
                />
              </div>
              <div className={styles.fieldRow}>
                <label className={styles.label}>Currency</label>
                <select
                  className={styles.select}
                  value={plannedBudgetCurrency}
                  onChange={(e) => setPlannedBudgetCurrency(e.target.value)}
                >
                  {COMMON_CURRENCIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                  {/* Preserve any non-standard existing value */}
                  {plannedBudgetCurrency &&
                    !COMMON_CURRENCIES.includes(plannedBudgetCurrency) && (
                      <option value={plannedBudgetCurrency}>{plannedBudgetCurrency}</option>
                    )}
                </select>
              </div>
            </div>
            <div className={styles.helpText}>
              Leave amount blank if the project doesn't have a planned budget yet.
            </div>
          </section>

          {/* ── 4. Status ── */}
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Status</h3>

            <div className={styles.fieldRow}>
              <label className={styles.label}>Lifecycle</label>
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <span className={styles.statusBadge}>
                  {LIFECYCLE_LABEL[miniProject.status] || miniProject.status}
                </span>
                <div className={styles.transitionRow}>
                  {transitionButtons.map((b) => (
                    <button
                      key={b.target}
                      type="button"
                      className={`${styles.transitionButton}${b.variant === "reverse" ? " " + styles.transitionButtonReverse : ""}`}
                      onClick={() => {
                        // U1b: completion routes to the rich dialog when the
                        // page provides it; everything else stays inline.
                        if (b.target === "complete" && onRequestComplete) {
                          onRequestComplete();
                        } else {
                          doTransition(b.target);
                        }
                      }}
                      disabled={busyAction !== null}
                    >
                      {b.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className={styles.helpText}>
                Lifecycle changes happen immediately (not via Save). Activation
                has hard prerequisites; completing prompts a confirmation and can
                be reversed with Reopen.
              </div>
            </div>

            <div className={styles.fieldRow}>
              <label className={styles.label}>Project Status</label>
              <select
                className={styles.select}
                value={projectStatus}
                onChange={(e) => setProjectStatus(e.target.value)}
              >
                <option value="">(not set)</option>
                {PROJECT_STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <div className={styles.helpText}>
                User-facing label for the project's execution state. Distinct from lifecycle
                (which is the backend state machine) and from Content Status (which is about
                verification). Matches Full Project vocabulary for cross-tier consistency.
              </div>
            </div>

            <div className={styles.fieldRow}>
              <label className={styles.label}>Content Status (Verification)</label>
              <select
                className={styles.select}
                value={contentStatus}
                onChange={(e) => setContentStatus(e.target.value)}
              >
                <option value="">(not assessed)</option>
                {CONTENT_STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <div className={styles.helpText}>
                Tracks where the project's content sits in the verification workflow
                (e.g. Drafting → Under Review → Peer Reviewed).
              </div>
            </div>

            {/* Task #218. Metadata Schema picker. Selecting a schema here
                drives the field set rendered on the Metadata tab. */}
            <div className={styles.fieldRow}>
              <label className={styles.label}>Metadata Schema</label>
              <select
                className={styles.select}
                value={metadataSchemaGuid}
                onChange={(e) => setMetadataSchemaGuid(e.target.value)}
              >
                <option value="">(none)</option>
                {schemaOptions.map((s: any) => (
                  <option key={s.schema_guid} value={s.schema_guid}>
                    {s.schema_name}
                  </option>
                ))}
              </select>
              <div className={styles.helpText}>
                Choose a schema to enable schema-driven fields on the "Metadata" tab.
                Schemas are authored on Communities; the picker shows every schema
                visible to you.
              </div>
            </div>
          </section>

          {/* ── 5. Map pin placement (Leaflet) + Lat/Lon ── */}
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Map Pin (Optional)</h3>
            <div className={styles.fieldRow}>
              <label className={styles.label}>Pin Placement</label>
              <div
                ref={miniMapContainerRef}
                style={{
                  inlineSize: "100%",
                  blockSize: 280,
                  borderRadius: 8,
                  overflow: "hidden",
                  border: "1px solid var(--border-default)",
                  background: "var(--bg-surface)",
                }}
              />
              <div className={styles.helpText}>
                Click the map to place the pin or drag it to adjust. This updates
                Latitude / Longitude below and is used by the portfolio map view.
              </div>
            </div>
            <div className={styles.grid2}>
              <div className={styles.fieldRow}>
                <label className={styles.label}>Latitude</label>
                <input
                  className={styles.input}
                  type="number"
                  step="any"
                  value={locationLat}
                  onChange={(e) => setLocationLat(e.target.value)}
                  placeholder="e.g. 42.3601"
                />
              </div>
              <div className={styles.fieldRow}>
                <label className={styles.label}>Longitude</label>
                <input
                  className={styles.input}
                  type="number"
                  step="any"
                  value={locationLon}
                  onChange={(e) => setLocationLon(e.target.value)}
                  placeholder="e.g. -71.0589"
                />
              </div>
            </div>
            <div className={styles.helpText}>
              Direct numeric entry is also supported. Leave blank to inherit from the
              Primary Location's centroid.
            </div>
          </section>

          </>
          )}

          {activeTab === "visibility" && (
          <>
          {/* ── Visibility tab ── */}
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Visibility</h3>

            <div className={styles.fieldRow}>
              <label className={styles.label}>Mini-Project Visibility</label>
              <select
                className={styles.select}
                value={visibility}
                onChange={(e) => {
                  const next = e.target.value as Visibility;
                  setVisibility(next);
                  // Auto-clamp progress visibility client-side so the
                  // disabled options below update immediately.
                  if (VIS_ORDINAL[progressVisibility] > VIS_ORDINAL[next]) {
                    setProgressVisibility(next);
                  }
                }}
              >
                {VISIBILITY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <div className={styles.helpText}>
                Team Only — team members + community admins + sysadmin.
                Community — any community member.
                Public — any logged-in L4E user.
              </div>
            </div>

            <div className={styles.fieldRow}>
              <label className={styles.label}>Progress Tab Visibility</label>
              <select
                className={styles.select}
                value={progressVisibility}
                onChange={(e) => setProgressVisibility(e.target.value as Visibility)}
              >
                {VISIBILITY_OPTIONS.map((opt) => {
                  const disabled = VIS_ORDINAL[opt.value] > mpOrdinal;
                  return (
                    <option key={opt.value} value={opt.value} disabled={disabled}>
                      {opt.label}{disabled ? " — raise MP visibility first" : ""}
                    </option>
                  );
                })}
              </select>
              <div className={styles.helpText}>
                Independent control over the Progress tab. Cannot exceed the Mini-Project
                visibility (currently {visibility}).
              </div>
            </div>
          </section>

          </>
          )}

          {activeTab === "team" && (
          <>
          {/* ── Team tab ──
              POLICY_TEAM_ROSTERS.md §3. Lead + Members management.
              Lead-cannot-be-hidden, 10-cap, transfer-Lead via dedicated
              affordance. In-memory store accumulates edits; parent Save
              persists via bulkSaveMiniProjectTeamMembers. */}
          <MiniProjectTeamTab
            miniProjectGuid={miniProject.miniProjectGuid}
            currentUserGuid={currentUserGuid}
            onDirty={handleTeamDirty}
          />
          </>
          )}

          {activeTab === "metadata" && (
          <>
          {/* ── Metadata tab (Task #219) ──
              Schema-driven value entry. The schema itself is selected on
              the General tab; this tab renders whatever fields the chosen
              schema declares. Edits accumulate in the tab's local draft
              and persist via batchUpsertMiniProjectCustomMetadata when
              the modal's Save button is clicked. */}
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Community Metadata</h3>
            <MiniProjectMetadataTab
              ref={metadataTabRef}
              miniProjectGuid={miniProject.miniProjectGuid}
              schemaGuid={metadataSchemaGuid}
              onDirty={setMetadataDirty}
            />
          </section>
          </>
          )}

          {activeTab === "general" && (
          <>
          {/* ── Project Info (read-only, General tab tail) ── */}
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Project Info</h3>
            <div className={styles.fieldRow}>
              <label className={styles.label}>Lead</label>
              <div className={styles.readOnlyValue}>{leadDisplayName}</div>
            </div>
            {miniProject.playbookGuid && (
              <div className={styles.fieldRow}>
                <label className={styles.label}>Playbook</label>
                <div className={styles.readOnlyValue}>
                  Created from Playbook v{miniProject.playbookVersion}
                </div>
                <div className={styles.helpText}>
                  Instance is frozen at this Playbook version. Updates to the Playbook
                  do not propagate to this Mini-Project.
                </div>
              </div>
            )}
          </section>

          </>
          )}

          {activeTab === "danger" && (
          <>
          {/* ── Danger Zone tab ── */}
          <section className={styles.dangerSection}>
            <h3 className={styles.dangerTitle}>Danger Zone</h3>
            {isArchived ? (
              <>
                <p className={styles.dangerDescription}>
                  This Mini-Project is currently archived (hidden from default lists).
                  Restoring brings it back to all the normal views; lifecycle status is
                  unaffected.
                </p>
                <button
                  type="button"
                  className={styles.dangerButton}
                  onClick={doUnarchive}
                  disabled={busyAction !== null}
                >
                  Restore from Archive
                </button>
              </>
            ) : (
              <>
                <p className={styles.dangerDescription}>
                  Archive hides this Mini-Project from default lists. The project's
                  status ({miniProject.status}) stays the same and the archive can be
                  reversed at any time.
                </p>
                <button
                  type="button"
                  className={styles.dangerButton}
                  onClick={doArchive}
                  disabled={busyAction !== null}
                >
                  Archive
                </button>
              </>
            )}
          </section>
          </>
          )}
        </div>

        {/* ── Footer (sticky) ── */}
        <footer className={styles.footer}>
          {error ? (
            <span className={styles.footerError}>Error: {error}</span>
          ) : saving ? (
            <span className={styles.footerSaving}>Saving…</span>
          ) : (
            <span style={{ flex: 1 }} />
          )}
          <button type="button" className={styles.cancelButton} onClick={handleCancel}>
            Cancel
          </button>
          <button
            type="button"
            className={styles.saveButton}
            onClick={handleSave}
            disabled={saving || !isDirty}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </footer>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
